import { useTracks } from "@livekit/components-react";
import { Track } from "livekit-client";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { sortTracksByActivity } from "@/lib/livekit-utils";
import { cn } from "@/lib/utils";
import { CustomParticipantTile } from "./custom-participant-tile";
import { MoveNotificationOverlay } from "./move-notification-overlay";

const TILES_PER_PAGE = 12;
const GAP = 8;
const ASPECT_RATIO = 16 / 9;

/**
 * Find the grid layout (cols x rows) with the largest tile area for the given
 * container dimensions. Adapts to both landscape and portrait.
 */
function computeOptimalGrid(
	n: number,
	containerWidth: number,
	containerHeight: number
): { layout: number[]; tileWidth: number; tileHeight: number } {
	if (n <= 0) return { layout: [], tileWidth: 0, tileHeight: 0 };

	let bestArea = 0;
	let bestCols = 1;
	let bestTileW = 0;
	let bestTileH = 0;

	for (let cols = 1; cols <= n; cols++) {
		const rows = Math.ceil(n / cols);

		const maxW = (containerWidth - GAP * (cols - 1)) / cols;
		const hFromW = maxW / ASPECT_RATIO;

		const maxH = (containerHeight - GAP * (rows - 1)) / rows;
		const wFromH = maxH * ASPECT_RATIO;

		// Take the smaller constraint so tiles fit both ways.
		const tileW = Math.min(maxW, wFromH);
		const tileH = Math.min(hFromW, maxH);

		const area = tileW * tileH;
		if (tileW > 0 && tileH > 0 && area > bestArea) {
			bestArea = area;
			bestCols = cols;
			bestTileW = tileW;
			bestTileH = tileH;
		}
	}

	// Distribute the tiles evenly across rows.
	const rows = Math.ceil(n / bestCols);
	const layout: number[] = [];
	let remaining = n;
	for (let r = 0; r < rows; r++) {
		const rowSize = Math.min(bestCols, remaining);
		layout.push(rowSize);
		remaining -= rowSize;
	}

	return { layout, tileWidth: bestTileW, tileHeight: bestTileH };
}

type CallVideoGridProps = {
	variant?: "full" | "compact";
};

export function CallVideoGrid({ variant = "full" }: CallVideoGridProps) {
	const trackSources = useMemo(
		() => [
			{ source: Track.Source.Camera, withPlaceholder: true },
			{ source: Track.Source.ScreenShare, withPlaceholder: false },
		],
		[]
	);

	const rawTracks = useTracks(trackSources, { onlySubscribed: false });
	const tracks = useMemo(() => sortTracksByActivity(rawTracks), [rawTracks]);

	const screenShareTrack = useMemo(
		() => tracks.find((track) => track.source === Track.Source.ScreenShare),
		[tracks]
	);

	const cameraTracks = useMemo(
		() => tracks.filter((track) => track.source !== Track.Source.ScreenShare),
		[tracks]
	);

	const sidebarWidth = variant === "compact" ? "w-24" : "w-40";

	const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
	const nodeRef = useRef<HTMLDivElement | null>(null);
	const observerRef = useRef<ResizeObserver | null>(null);

	const measure = useCallback(() => {
		const el = nodeRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		setContainerSize((prev) => {
			if (prev.width === rect.width && prev.height === rect.height) return prev;
			return { width: rect.width, height: rect.height };
		});
	}, []);

	const containerRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (observerRef.current) {
				observerRef.current.disconnect();
				observerRef.current = null;
			}
			nodeRef.current = node;
			if (!node) return;

			const rect = node.getBoundingClientRect();
			setContainerSize({ width: rect.width, height: rect.height });

			observerRef.current = new ResizeObserver(() => measure());
			observerRef.current.observe(node);
		},
		[measure]
	);

	// A window resize listener as fallback, because PiP windows do not fire
	// ResizeObserver reliably. The element's ownerDocument gives the right window,
	// which is what makes this work inside a PiP portal.
	useEffect(() => {
		const el = nodeRef.current;
		const win = el?.ownerDocument.defaultView ?? window;
		const onResize = () => measure();
		win.addEventListener("resize", onResize);
		return () => {
			win.removeEventListener("resize", onResize);
			if (observerRef.current) {
				observerRef.current.disconnect();
			}
		};
	}, [measure]);

	const [currentPage, setCurrentPage] = useState(0);
	const totalPages = Math.max(
		1,
		Math.ceil(cameraTracks.length / TILES_PER_PAGE)
	);

	useEffect(() => {
		if (currentPage >= totalPages) {
			setCurrentPage(Math.max(0, totalPages - 1));
		}
	}, [currentPage, totalPages]);

	const pageTracks = useMemo(() => {
		const start = currentPage * TILES_PER_PAGE;
		return cameraTracks.slice(start, start + TILES_PER_PAGE);
	}, [cameraTracks, currentPage]);

	const { layout, tileWidth, tileHeight } = useMemo(() => {
		if (containerSize.width === 0 || containerSize.height === 0) {
			return { layout: [], tileWidth: 0, tileHeight: 0 };
		}
		const paginationHeight = totalPages > 1 ? 48 : 0;
		return computeOptimalGrid(
			pageTracks.length,
			containerSize.width,
			containerSize.height - paginationHeight
		);
	}, [pageTracks.length, containerSize, totalPages]);

	if (screenShareTrack) {
		return (
			<div className="relative flex h-full w-full flex-1 gap-0 overflow-hidden bg-muted">
				<Card className="relative flex flex-1 items-center justify-center gap-0 overflow-hidden p-0">
					<div className="[&_.lk-participant-tile]:!border-0 h-full w-full [&_.lk-participant-metadata]:hidden [&_.lk-participant-name]:hidden [&_.lk-participant-tile]:h-full [&_.lk-participant-tile]:w-full [&_video]:h-full [&_video]:w-full [&_video]:object-contain">
						<CustomParticipantTile trackRef={screenShareTrack} />
					</div>
				</Card>
				<ScrollArea className={cn(sidebarWidth, "shrink-0")}>
					<div className="flex flex-col gap-0 p-0">
						{cameraTracks.map((track) => (
							<div
								className={cn(
									"aspect-video w-full shrink-0 overflow-hidden rounded-lg border bg-card",
									"[&_.lk-participant-name]:mx-1 [&_.lk-participant-name]:mb-1 [&_.lk-participant-name]:max-w-full",
									"[&_.lk-participant-name]:overflow-hidden [&_.lk-participant-name]:text-ellipsis [&_.lk-participant-name]:whitespace-nowrap",
									"[&_.lk-participant-name]:text-white [&_.lk-participant-name]:text-xs",
									"[&_.lk-participant-tile]:h-full [&_.lk-participant-tile]:w-full [&_.lk-participant-tile]:max-w-none"
								)}
								key={`${track.participant.sid}-${track.source}`}
							>
								<CustomParticipantTile trackRef={track} />
							</div>
						))}
					</div>
				</ScrollArea>
				<MoveNotificationOverlay />
			</div>
		);
	}

	let trackIndex = 0;
	const rows = layout.map((cols, rowIdx) => {
		const rowTracks = pageTracks.slice(trackIndex, trackIndex + cols);
		trackIndex += cols;
		return (
			<div
				className="flex items-center justify-center"
				key={rowIdx}
				style={{ gap: GAP }}
			>
				{rowTracks.map((track) => (
					<div
						className="overflow-hidden rounded-lg border bg-card"
						key={`${track.participant.sid}-${track.source}`}
						style={{
							width: tileWidth,
							height: tileHeight,
						}}
					>
						<div className="h-full w-full [&_.lk-participant-name]:mx-1 [&_.lk-participant-name]:mb-1 [&_.lk-participant-name]:max-w-full [&_.lk-participant-name]:overflow-hidden [&_.lk-participant-name]:text-ellipsis [&_.lk-participant-name]:whitespace-nowrap [&_.lk-participant-name]:text-white [&_.lk-participant-name]:text-xs [&_.lk-participant-tile]:h-full [&_.lk-participant-tile]:w-full [&_.lk-participant-tile]:max-w-none">
							<CustomParticipantTile trackRef={track} />
						</div>
					</div>
				))}
			</div>
		);
	});

	return (
		<div
			className="relative min-h-0 w-full flex-1 overflow-hidden bg-muted"
			ref={containerRef}
		>
			<div
				className="absolute inset-0 flex flex-col items-center justify-center"
				style={{ gap: GAP }}
			>
				{rows}
			</div>

			{totalPages > 1 && (
				<div className="-translate-x-1/2 absolute bottom-2 left-1/2 flex items-center gap-2">
					<Button
						className="h-8 w-8"
						disabled={currentPage === 0}
						onClick={() => setCurrentPage((p) => p - 1)}
						size="icon"
						variant="secondary"
					>
						<ChevronLeft className="h-4 w-4" />
					</Button>
					<span className="text-muted-foreground text-sm">
						Page {currentPage + 1} of {totalPages}
					</span>
					<Button
						className="h-8 w-8"
						disabled={currentPage === totalPages - 1}
						onClick={() => setCurrentPage((p) => p + 1)}
						size="icon"
						variant="secondary"
					>
						<ChevronRight className="h-4 w-4" />
					</Button>
				</div>
			)}
			<MoveNotificationOverlay />
		</div>
	);
}
